/*
* Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
*/
import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {CheckmarkIcon} from '../../icons/CheckmarkIcon';
import {CopyIcon} from '../../icons/CopyIcon';
import {LinkCopyIcon} from '../../icons/LinkCopyIcon';
import {DLSTheme, IconSize} from '../../types/types';

type CopyButtonVariant = 'link' | 'plaintext';

export interface CopyButtonProps {
  onCopy: () => void;
  size?: IconSize;
  variant?: CopyButtonVariant;
}

export const useStyles = makeStyles((theme: DLSTheme) => {
  const getAnimation = (type: 'expand' | 'shrink') => ({
    animation : `$${type} 0.3s forwards`
  });
  return {
    copyButton : {
      background   : 'none',
      border       : 'none',
      cursor       : 'pointer',
      borderRadius : '4px',
      transition   : 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)'
    },
    copyIcon            : getAnimation('shrink'),
    checkmark           : getAnimation('expand'),
    copiedCheckmark     : getAnimation('expand'),
    copiedCopyIcon      : getAnimation('shrink'),
    '@keyframes shrink' : {
      '0%' : {
        transform : 'scale(0.5)'
      },
      '100%' : {
        transform : 'scale(1)'
      }
    },
    '@keyframes expand' : {
      '0%' : {
        transform : 'scale(0.5)'
      },
      '100%' : {
        transform : 'scale(1.5)'
      }
    }
  };
});

/**
 * The CopyButton component is a reusable button designed to trigger a "copy" action 
 * and provide user feedback indicating that content has been successfully copied. 
 * When clicked, the button changes its appearance to show a checkmark, indicating
 *  that the copy action has taken place, and it reverts back after a short delay.
 * 
 * ```typescript
 * import CopyButton from '@ion/dls/src/components/buttons/CopyButton';
 * 
 * const handleCopy = () => {
 *  copyToClipboard(link);
 * };
 * 
 * <SomeComponent>
 * ...
 *   <CopyButton onCopy={handleCopy} />
 * ...
 * </SomeComponent>
 * 
 * ```
 * 
 * @param onCopy - The function to be called when the button is clicked.
 * @param size - The size of the button. Default is IconSize.small.
 */
export const CopyButton = ({onCopy, size = IconSize.small, variant = 'link'}: CopyButtonProps) => {
  const classes                 = useStyles();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClick = function() {
    onCopy();
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1200);
  };

  const IconComponent = variant === 'link' ? LinkCopyIcon : CopyIcon;

  return (
    <button type='button' onClick={handleCopyClick} className={classes.copyButton}>
      {isCopied ? (
        <CheckmarkIcon className={classes.checkmark} size={size} />
      ) : (
        <IconComponent className={classes.copyIcon} size={size}/>
      )}
    </button>
  );
};

export default CopyButton;
